// pages/employees/profiles.tsx
import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Search,
  Filter,
  X,
  Eye,
  Users,
  User,
  Building,
  Briefcase,
  Calendar,
  DollarSign,
  Table,
  Grid,
} from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import api from "@/services/api";
import EmployeeProfileModal from "@/components/EmployeeProfileModal";

interface Employee {
  id: number;
  employeeNumber: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
  department: string;
  position: string;
  employmentStatus: "active" | "inactive" | "terminated";
  employmentType: "full_time" | "part_time" | "contract";
  baseSalary: string;
  salaryCurrency: "USD" | "CDF";
  hireDate: string;
  manager?: {
    id: number;
    firstName: string;
    lastName: string;
    employeeNumber: string;
  };
}

interface EmployeesResponse {
  employees: Employee[];
  pagination: { total: number };
}

interface Filters {
  department: string;
  employmentStatus: string;
  employmentType: string;
}

const EmployeeProfiles: React.FC = () => {
  const { user } = useAuth();
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [filters, setFilters] = useState<Filters>({
    department: "",
    employmentStatus: "",
    employmentType: "",
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState<"table" | "grid">("table");

  const { data, isLoading } = useQuery<EmployeesResponse>({
    queryKey: ["employees-profiles", searchTerm, filters],
    queryFn: async () => {
      const params = new URLSearchParams({
        limit: "50",
        ...(searchTerm && { search: searchTerm }),
        ...(filters.department && { department: filters.department }),
        ...(filters.employmentStatus && { employmentStatus: filters.employmentStatus }),
        ...(filters.employmentType && { employmentType: filters.employmentType }),
      });
      const response = await api.get(`/employees?${params}`);
      return response.data.data;
    },
  });

  const departments = useMemo(() => {
    if (!data?.employees) return [];
    return Array.from(new Set(data.employees.map((e) => e.department))).filter(Boolean);
  }, [data?.employees]);

  const handleProfileView = (employee: Employee) => {
    setSelectedEmployee(employee);
    setShowProfileModal(true);
  };

  const clearFilters = () => setFilters({ department: "", employmentStatus: "", employmentType: "" });

  const getStatusBadge = (status: string) => {
    const map: Record<string, string> = {
      active: "bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800",
      inactive: "bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800",
      terminated: "bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800",
    };

    const dotMap: Record<string, string> = {
      active: "bg-emerald-500",
      inactive: "bg-amber-500",
      terminated: "bg-red-500",
    };

    const classes = map[status] || map.inactive;
    const dotClasses = dotMap[status] || dotMap.inactive;

    return (
      <span className={`inline-flex items-center justify-center px-2 py-1 rounded text-xs font-medium border ${classes}`}>
        <div className={`h-1.5 w-1.5 rounded-full mr-1.5 ${dotClasses}`} />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const getTypeBadge = (type: string) => {
    const labels: Record<string, string> = {
      full_time: "Full Time",
      part_time: "Part Time",
      contract: "Contract",
    };

    return (
      <span className="inline-flex items-center justify-center px-2 py-1 rounded text-xs font-medium border bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-800">
        <div className="h-1.5 w-1.5 rounded-full mr-1.5 bg-blue-500" />
        {labels[type]}
      </span>
    );
  };

  const formatSalary = (salary: string, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(parseFloat(salary));
  };

  const getTenure = (hireDate: string) => {
    const hire = new Date(hireDate);
    const now = new Date();
    const years = now.getFullYear() - hire.getFullYear();
    const months = now.getMonth() - hire.getMonth();

    if (months < 0) {
      return `${years - 1}y ${12 + months}m`;
    }
    return `${years}y ${months}m`;
  };

  const inputClasses = "border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white";

  if (!user) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="h-12 w-12 mx-auto bg-indigo-600 rounded-lg flex items-center justify-center mb-3">
            <User className="text-white h-6 w-6" />
          </div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Access Required</h3>
          <p className="text-xs text-gray-600 dark:text-gray-300">
            Please log in to view employee profiles.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-5">
      {/* Page Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-3 mb-4 lg:mb-0">
          <div className="h-10 w-10 bg-indigo-600 rounded-lg flex items-center justify-center">
            <Users size={20} className="text-white" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-gray-900 dark:text-white">Employee Profiles</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Browse and explore detailed profiles of all company employees
            </p>
          </div>
        </div>
        <div className="flex gap-3">
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-card px-4 py-2">
            <div className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Total Profiles</div>
            <div className="text-sm font-bold text-gray-900 dark:text-white">
              {data?.pagination?.total || 0}
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-card px-4 py-2">
            <div className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Active Employees</div>
            <div className="text-sm font-bold text-gray-900 dark:text-white">
              {data?.employees?.filter(e => e.employmentStatus === 'active').length || 0}
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-card p-4">
        <div className="flex flex-col md:flex-row gap-3 items-center">
          <div className="flex-1 relative w-full">
            <Search className="absolute left-3 top-3 text-gray-400 h-3 w-3" />
            <input
              type="text"
              placeholder="Search employees by name, email, department, or position..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full pl-9 pr-4 py-2 ${inputClasses} placeholder-gray-500 dark:placeholder-gray-400 text-xs`}
            />
          </div>

          {/* View Mode Toggle */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setViewMode("table")}
              className={`p-2 rounded-lg border transition-all duration-200 ${
                viewMode === "table"
                  ? "bg-indigo-600 text-white border-indigo-600"
                  : "bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
              }`}
              title="Table View"
            >
              <Table size={14} />
            </button>
            <button
              onClick={() => setViewMode("grid")}
              className={`p-2 rounded-lg border transition-all duration-200 ${
                viewMode === "grid"
                  ? "bg-indigo-600 text-white border-indigo-600"
                  : "bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
              }`}
              title="Grid View"
            >
              <Grid size={14} />
            </button>
          </div>

          <button
            onClick={() => setShowFilters(!showFilters)}
            className="inline-flex items-center gap-2 px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-all"
          >
            <Filter size={12} /> Filters
          </button>
        </div>

        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="grid md:grid-cols-3 gap-3">
              <select
                value={filters.department}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, department: e.target.value }))
                }
                className={`px-3 py-2 ${inputClasses} text-xs`}
              >
                <option value="">All Departments</option>
                {departments.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
              <select
                value={filters.employmentStatus}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, employmentStatus: e.target.value }))
                }
                className={`px-3 py-2 ${inputClasses} text-xs`}
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="terminated">Terminated</option>
              </select>
              <select
                value={filters.employmentType}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, employmentType: e.target.value }))
                }
                className={`px-3 py-2 ${inputClasses} text-xs`}
              >
                <option value="">All Types</option>
                <option value="full_time">Full Time</option>
                <option value="part_time">Part Time</option>
                <option value="contract">Contract</option>
              </select>
            </div>
            <div className="flex justify-end mt-3">
              <button
                onClick={clearFilters}
                className="flex items-center gap-1 text-xs font-medium text-red-600 hover:text-red-700 transition-colors"
              >
                <X size={10} /> Clear all filters
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Table View */}
      {viewMode === "table" && !isLoading && data?.employees && data.employees.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="data-table min-w-full text-xs">
              <thead className="bg-gray-50 dark:bg-gray-700/50 text-gray-900 dark:text-white uppercase font-semibold">
                <tr>
                  <th className="px-4 py-3 text-left whitespace-nowrap">Employee</th>
                  <th className="px-4 py-3 text-left whitespace-nowrap">Department</th>
                  <th className="px-4 py-3 text-left whitespace-nowrap">Position</th>
                  <th className="px-4 py-3 text-left whitespace-nowrap">Type</th>
                  <th className="px-4 py-3 text-left whitespace-nowrap">Status</th>
                  <th className="px-4 py-3 text-left whitespace-nowrap">Salary</th>
                  <th className="px-4 py-3 text-left whitespace-nowrap">Tenure</th>
                  <th className="px-4 py-3 text-right whitespace-nowrap">Actions</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {data.employees.map((employee) => (
                  <tr key={employee.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-200">
                    {/* Employee */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 bg-indigo-600 rounded-lg flex items-center justify-center font-semibold text-white text-xs">
                          {employee.firstName[0]}
                          {employee.lastName[0]}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white text-xs">
                            {employee.firstName} {employee.lastName}
                          </p>
                          <p className="text-gray-500 dark:text-gray-400 text-xs">#{employee.employeeNumber}</p>
                        </div>
                      </div>
                    </td>

                    {/* Department */}
                    <td className="px-4 py-3 text-gray-700 dark:text-gray-300 text-xs font-medium">
                      {employee.department}
                    </td>

                    {/* Position */}
                    <td className="px-4 py-3 text-gray-700 dark:text-gray-300 text-xs">
                      {employee.position}
                    </td>

                    {/* Type */}
                    <td className="px-4 py-3">
                      {getTypeBadge(employee.employmentType)}
                    </td>

                    {/* Status */}
                    <td className="px-4 py-3">
                      {getStatusBadge(employee.employmentStatus)}
                    </td>

                    {/* Salary */}
                    <td className="px-4 py-3 font-semibold text-xs text-emerald-700 dark:text-emerald-400">
                      {formatSalary(employee.baseSalary, employee.salaryCurrency)}
                    </td>

                    {/* Tenure */}
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400 text-xs">
                      {getTenure(employee.hireDate)}
                    </td>

                    {/* Actions */}
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => handleProfileView(employee)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium transition-all duration-200"
                      >
                        <Eye size={10} />
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Grid View */}
      {viewMode === "grid" && !isLoading && data?.employees && data.employees.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {data.employees.map((employee) => (
            <div
              key={employee.id}
              className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-card hover:shadow-md transition-all duration-300 overflow-hidden group cursor-pointer"
              onClick={() => handleProfileView(employee)}
            >
              {/* Card Header */}
              <div className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700 p-4">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 bg-indigo-600 rounded-lg flex items-center justify-center font-semibold text-white text-xs">
                    {employee.firstName[0]}
                    {employee.lastName[0]}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
                      {employee.firstName} {employee.lastName}
                    </h3>
                    <div className="flex items-center gap-2 mt-1">
                      <div className="text-gray-500 dark:text-gray-400 text-xs font-medium">
                        #{employee.employeeNumber}
                      </div>
                      {getStatusBadge(employee.employmentStatus)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Card Body */}
              <div className="p-4 space-y-3">
                {/* Department */}
                <div className="flex items-center text-xs text-gray-600 dark:text-gray-300">
                  <Building className="h-3 w-3 mr-2 text-indigo-600 dark:text-indigo-400" />
                  <span className="font-medium">{employee.department}</span>
                </div>

                {/* Position */}
                <div className="flex items-center text-xs text-gray-600 dark:text-gray-300">
                  <Briefcase className="h-3 w-3 mr-2 text-indigo-600 dark:text-indigo-400" />
                  <span>{employee.position}</span>
                </div>

                {/* Tenure */}
                <div className="flex items-center text-xs text-gray-600 dark:text-gray-300">
                  <Calendar className="h-3 w-3 mr-2 text-indigo-600 dark:text-indigo-400" />
                  <span>Tenure: {getTenure(employee.hireDate)}</span>
                </div>

                {/* Salary & Type */}
                <div className="flex items-center justify-between text-xs">
                  <div className="flex items-center text-gray-600 dark:text-gray-300">
                    <DollarSign className="h-3 w-3 mr-2 text-emerald-600 dark:text-emerald-400" />
                    <span className="font-semibold text-emerald-700 dark:text-emerald-400">
                      {formatSalary(employee.baseSalary, employee.salaryCurrency)}
                    </span>
                  </div>
                  {getTypeBadge(employee.employmentType)}
                </div>

                {/* Quick Actions */}
                <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleProfileView(employee);
                    }}
                    className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium transition-all duration-300"
                  >
                    <Eye size={10} />
                    View Full Profile
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {(!isLoading && (!data?.employees || data.employees.length === 0)) && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-card p-6 text-center">
          <div className="h-10 w-10 mx-auto bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center mb-3">
            <Users className="text-gray-400 h-5 w-5" />
          </div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
            No employee profiles found
          </h3>
          <p className="text-xs text-gray-600 dark:text-gray-300 mb-4">
            {searchTerm || Object.values(filters).some(Boolean)
              ? "Try adjusting your search or filters to find what you're looking for."
              : "No employees have been added to the system yet."
            }
          </p>
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-card p-6">
          <div className="text-center">
            <div className="h-8 w-8 rounded-full border-[3px] border-indigo-600 border-t-transparent animate-spin mx-auto mb-3" />
            <p className="text-xs text-gray-600 dark:text-gray-300">Loading employee profiles...</p>
          </div>
        </div>
      )}

      {/* Profile Modal */}
      {showProfileModal && selectedEmployee && (
        <EmployeeProfileModal
          employee={selectedEmployee}
          onClose={() => {
            setShowProfileModal(false);
            setSelectedEmployee(null);
          }}
        />
      )}
    </div>
  );
};

export default EmployeeProfiles;